import axios, { type AxiosError } from 'axios';
import { pick } from 'lodash';

import { CSRF_TOKEN } from '@shared/config/csrf';

import {
  ORDER_FORM_API_SEGMENTS,
  ORDER_FORM_ATTRIBUTES,
  type OrderFormType,
} from '@admin/constants/order_form_attributes';
import { serializeOrder } from '@admin/utils/order_serializer';

const requestHeaders = () => ({
  'X-CSRF-Token': CSRF_TOKEN ?? '',
});

export type SavedOrder = {
  id: number;
  account_id: number;
};

export const saveOrder = async ({
  formType,
  accountID,
  order,
}: {
  formType: OrderFormType;
  accountID: number | string;

  order: Record<string, any>;
}): Promise<SavedOrder> => {
  const segment = ORDER_FORM_API_SEGMENTS[formType];
  const attributes = ORDER_FORM_ATTRIBUTES[formType];
  const payload = pick(serializeOrder(order), attributes);
  const url = order.id
    ? `/accounts/${accountID}/${segment}/${order.id}.json`
    : `/accounts/${accountID}/${segment}.json`;

  const response = order.id
    ? await axios.patch<SavedOrder>(url, payload, { headers: requestHeaders() })
    : await axios.post<SavedOrder>(url, payload, { headers: requestHeaders() });

  return response.data;
};

export const classifyOrderSubtype = async ({
  accountID,
  formType,
  cancelable,
  subtype,
}: {
  accountID: number | string;
  formType: OrderFormType;
  cancelable?: boolean;
  subtype?: string;
}): Promise<{ subtype?: string }> => {
  const params = new URLSearchParams({
    type: formType,
    cancelable: String(!!cancelable),
  });
  if (subtype) {
    params.set('subtype', subtype);
  }

  const response = await axios.get<{ subtype?: string }>(
    `/accounts/${accountID}/orders/classify.json?${params.toString()}`,
  );
  return response.data;
};

export const fetchAccountOnboarded = async (accountID: number | string): Promise<boolean> => {
  const response = await axios.get<{ onboarded?: boolean }>(`/accounts/${accountID}/onboarded.json`);
  return !!response.data.onboarded;
};

export const estimateReturnMovers = async ({
  itemIds,
  addressId,
}: {
  itemIds: number[];
  addressId?: number;
}): Promise<number> => {
  const response = await axios.post<{ estimated_mover_count: number }>(
    '/return_movers_estimations.json',
    {
      item_ids: itemIds,
      address_id: addressId,
    },
    { headers: requestHeaders() },
  );
  return response.data.estimated_mover_count;
};

export const orderFormErrorMessage = (error: unknown): string => {
  const axiosError = error as AxiosError<{ message?: string }>;
  if (axiosError?.response?.data?.message) {
    return axiosError.response.data.message;
  }

  switch (axiosError?.response?.status) {
    case 403:
      return "Forbidden - You don't have permission to perform this action";
    case 500:
      return 'The server encountered an unexpected error. Please try again shortly.';
    case 503:
      return 'The server is undergoing maintenance. Please try again shortly.';
    default:
      break;
  }

  if (axiosError?.message) {
    return axiosError.message;
  }

  return 'An unknown error occurred. Please contact technical support.';
};
